import { useEffect, useMemo, useRef } from 'react';
import { AllocationScenarioValuationModel, OPMCalculator } from 'api';
import { ScalarSpreadsheetConfigs } from 'common/types/scalarSpreadsheet';
import { ValuationsApproach } from 'common/types/valuation';
import { getStringValue } from 'utilities';
import type { OPMCalculatorInputs, OPMCalculatorLinkedSources } from '../../approaches/OPMCalculator/types';
import { EQUITY_ALLOCATION_LABEL } from '../../common/constants/equityAllocation';
import { toOPMCalculatorSourceFields } from '../../components/ShowOPMCalculationsLink/opmCalculatorLinkSources';
import readOPMCalculatorInputs from '../../components/ShowOPMCalculationsLink/readOPMCalculatorInputs';
import useOPMCalculatorDecimalPlaces from '../../components/ShowOPMCalculationsLink/useOPMCalculatorDecimalPlaces';
import { getOPMCalculatorPanelId } from '../../util/getOPMCalculatorPanelId';
import { resolveLinkedOPMCalculatorSource } from '../../util/resolveLinkedOPMCalculatorSource';
import {
  buildOPMCalculatorInputsPatch,
  getComparableOPMCalculatorFields,
} from '../useCreateOPMCalculator/useCreateOPMCalculator';

const INPUT_KEYS = ['capTableId', 'equityValue', 'riskFreeRate', 'maturity', 'volatility', 'opmDate'] as const;

const FALLBACK_LABELS: Record<OPMCalculator.source_type, string> = {
  [OPMCalculator.source_type.BACKSOLVE]: 'Backsolve',
  [OPMCalculator.source_type.FUTURE_EXIT]: 'Future Exit',
  [OPMCalculator.source_type.EQUITY_ALLOCATION]: EQUITY_ALLOCATION_LABEL,
};

interface UseSyncLinkedOPMCalculatorsParams {
  opmCalculators?: OPMCalculator[] | null;
  approaches: ValuationsApproach[];
  allocationScenarios?: AllocationScenarioValuationModel[] | null;
  workbook?: ScalarSpreadsheetConfigs | null;
  /** False while approaches are synchronizing or the workbook is still building. */
  isReady: boolean;
  /** Writes the patches onto the rows without flagging unsaved changes. */
  applySync: (patches: Map<string, Partial<OPMCalculator>>) => void;
}

/**
 * Keeps every linked OPM Calculator -- one a "Show OPM Calculations" link opened -- on the numbers
 * its source (Backsolve, Future Exit, Equity Allocation scenario) currently prices with. A
 * calculator added from the "Add" dialog has no source and is never touched.
 *
 * Mounted with `ValuationApproaches`, so the calculator follows its source whichever tab is
 * open. Only fields that actually differ are written, so a pass with nothing new is a no-op (no
 * render loop). A source that is gone or no longer prices with OPM unlinks the calculator, which
 * keeps its last values and becomes editable.
 *
 * Display only: nothing here is an unsaved change. The calculator is saved with its source --
 * the backend's `sync_linked_opm_calculators` rewrites linked rows from the saved source on
 * every valuation save -- so editing a Backsolve maturity needs the one Save it always did.
 *
 * Also returns, per panel, what the calculator page needs: the caption label and which inputs
 * are the source's (read-only there).
 */
const useSyncLinkedOPMCalculators = ({
  opmCalculators,
  approaches,
  allocationScenarios,
  workbook,
  isReady,
  applySync,
}: UseSyncLinkedOPMCalculatorsParams): OPMCalculatorLinkedSources => {
  const decimalPlaces = useOPMCalculatorDecimalPlaces();

  const resolutions = useMemo(
    () =>
      (opmCalculators ?? [])
        .filter(opmCalculator => opmCalculator.source_type)
        .map(opmCalculator => {
          const resolution = isReady
            ? resolveLinkedOPMCalculatorSource({
              opmCalculator,
              approaches,
              allocationScenarios: allocationScenarios ?? [],
              workbook,
            })
            : ({ status: 'pending' } as const);
          const sourceInputs
            = resolution.status === 'linked' ? readOPMCalculatorInputs(workbook, resolution.source, decimalPlaces) : undefined;
          return { opmCalculator, resolution, sourceInputs };
        }),
    [allocationScenarios, approaches, decimalPlaces, isReady, opmCalculators, workbook]
  );

  // Latest callback without re-running the sync when only its identity changes.
  const applySyncRef = useRef(applySync);
  applySyncRef.current = applySync;

  useEffect(() => {
    if (!isReady || resolutions.length === 0 || !opmCalculators) {
      return;
    }

    const patches = new Map<string, Partial<OPMCalculator>>();
    resolutions.forEach(({ opmCalculator, resolution, sourceInputs }) => {
      const panelId = getOPMCalculatorPanelId(opmCalculator);

      if (resolution.status === 'gone') {
        patches.set(panelId, toOPMCalculatorSourceFields(undefined));
        return;
      }
      if (resolution.status !== 'linked' || !sourceInputs) {
        return;
      }

      // Only inputs the source supplies; one it lacks stays the analyst's.
      const supplied = Object.fromEntries(
        INPUT_KEYS.filter(key => sourceInputs[key] !== undefined).map(key => [key, sourceInputs[key]])
      ) as Partial<OPMCalculatorInputs>;
      const patch = buildOPMCalculatorInputsPatch(supplied);
      const current = getComparableOPMCalculatorFields(opmCalculator, decimalPlaces);
      const next = getComparableOPMCalculatorFields({ ...opmCalculator, ...patch }, decimalPlaces);
      const isStale = (Object.keys(current) as Array<keyof typeof current>).some(key => current[key] !== next[key]);

      if (isStale) {
        patches.set(panelId, patch);
      }
    });

    if (patches.size === 0) {
      return;
    }

    applySyncRef.current(patches);
  }, [decimalPlaces, isReady, opmCalculators, resolutions]);

  return useMemo(
    () =>
      Object.fromEntries(
        resolutions
          .filter(({ resolution }) => resolution.status !== 'gone')
          .map(({ opmCalculator, resolution, sourceInputs }) => {
            const sourceType = opmCalculator.source_type as OPMCalculator.source_type;
            const label
              = resolution.status === 'linked'
                ? getStringValue(resolution.source.label) || FALLBACK_LABELS[sourceType]
                : FALLBACK_LABELS[sourceType];
            // Only lock once the source has resolved: pending sheets would otherwise freeze
            // every filled cell, including ones the source never supplies.
            const lockedInputs
              = resolution.status === 'linked' && sourceInputs
                ? INPUT_KEYS.filter(key => sourceInputs[key] !== undefined)
                : [];
            return [getOPMCalculatorPanelId(opmCalculator), { label, lockedInputs: [...lockedInputs] }];
          })
      ),
    [resolutions]
  );
};

export default useSyncLinkedOPMCalculators;
